import type { Client } from "../client";
import type { FeatureToggleResource, ProjectResource, ToggleStrategy, TenantTargetingStrategy, ResourceWithId } from "../resources";
export interface ListFeatureTogglesResponse {
    Features: FeatureToggleResource[];
}
export interface ModifyFeatureToggleCommand {
    Id: string;
    ProjectId: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Toggles: ToggleDefinition[];
}
export type CreateFeatureToggleCommand = Omit<ModifyFeatureToggleCommand, "Id" | "Slug" | "SpaceId">;
interface ModifyFeatureToggleResponse extends ResourceWithId {
    ProjectId: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Toggles: ToggleDefinition[];
}
interface ToggleDefinition {
    DeploymentEnvironmentId: string;
    IsEnabled: boolean;
    ToggleStrategy: ToggleStrategy;
    TenantTargetingStrategy: TenantTargetingStrategy;
    TenantIds: string[];
    RolloutPercentage?: number;
    Segments: string[];
}
export class FeatureToggleRepository {
    protected client: Client;
    protected apiBaseRoute: string = `~/api/spaces/{spaceId}/projects/{projectId}/featuretoggles`;
    constructor(client: Client) {
        this.client = client;
    }
    async list(project: ProjectResource) {
        return this.client.get<ListFeatureTogglesResponse>(this.apiBaseRoute, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
    }
    async get(project: ProjectResource, featureToggleSlug: string) {
        return this.client.get<FeatureToggleResource>(`${this.apiBaseRoute}/{featureToggleSlug}`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            featureToggleSlug,
        });
    }
    async create(project: ProjectResource, command: CreateFeatureToggleCommand): Promise<FeatureToggleResource> {
        const response = await this.client.create<CreateFeatureToggleCommand, ModifyFeatureToggleResponse>(this.apiBaseRoute, command, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
        return await this.get(project, response.Slug);
    }
    async modify(project: ProjectResource, resource: ModifyFeatureToggleCommand) {
        const response = await this.client.update<ModifyFeatureToggleCommand>(this.apiBaseRoute, resource, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
        return await this.get(project, response.Slug);
    }
    async delete(project: ProjectResource, featureToggleId: string) {
        await this.client.del(`${this.apiBaseRoute}/{featureToggleId}`, null, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            featureToggleId,
        });
    }
}
